import React from 'react';
import './CustomModal.css';

/**
 * Componente genérico de modal personalizado
 * @param {Object} props
 * @param {string} props.type - Tipo de modal: 'confirm', 'success', 'warning', 'error'
 * @param {string} props.title - Título del modal
 * @param {string|ReactNode} props.message - Mensaje principal del modal
 * @param {string} props.headerColor - Color del header (hex, rgb, o nombre de color)
 * @param {string} props.icon - Clase del icono Font Awesome Thin (ej: 'fa-comments', 'fa-trash-can', 'fa-check-circle')
 * @param {string} props.primaryButtonText - Texto del botón principal
 * @param {string} props.secondaryButtonText - Texto del botón secundario (opcional)
 * @param {Function} props.onPrimaryClick - Callback del botón principal
 * @param {Function} props.onSecondaryClick - Callback del botón secundario
 * @param {Function} props.onClose - Callback para cerrar el modal
 * @param {boolean} props.showCloseButton - Mostrar botón de cerrar (X) en el header
 * @param {string} props.primaryButtonColor - Color del botón principal (por defecto usa el headerColor)
 * @param {string} props.secondaryMessage - Mensaje secundario opcional (solo para tipo confirm)
 */
const CustomModal = ({
  type = 'confirm',
  title = '¡Confirmar Acción!',
  message = 'Tu solicitud se ha procesado correctamente.',
  headerColor,
  icon,
  primaryButtonText = 'Continuar',
  secondaryButtonText = 'Cancelar',
  tertiaryButtonText = 'Cerrar',
  onPrimaryClick,
  onSecondaryClick,
  onTertiaryClick,
  onClose,
  showCloseButton = true,
  primaryButtonColor,
  secondaryMessage = null
}) => {
  // Configuración por defecto según el tipo
  const getTypeConfig = () => {
    const configs = {
      confirm: {
        headerColor: headerColor || '#0D6EFD', 
        icon: icon || 'fa-messages-question',
        title: title || '¡Confirmar Acción!',
        showSecondary: true,
        buttonColor: '#0D6EFD',
        hoverColor: '#2557C2',
        border: 'none',
        hoverBorder: '1px solid white'
      },
      success: {
        headerColor: headerColor || '#28a745',
        icon: icon || 'fa-message-check',
        title: title || '¡Acción Completada!',
        showSecondary: false,
        buttonColor: '#2e7d32',
        hoverColor: '#336A46',
        border: 'none',
        hoverBorder: 'none'
      },
      warning: {
        headerColor: headerColor || '#ff9800', 
        icon: icon || 'fa-messages-question',
        title: title || '¡Confirmar Acción!',
        showSecondary:true,
        buttonColor: '#E1922E',
        hoverColor: '#C17C26',
        border: '1px solid #C17C26',
        hoverBorder: '1px solid white'
      },
      error: {
        headerColor: headerColor || '#d9534f', 
        icon: icon || 'fa-trash-can',
        title: title || '¡Confirmar Acción!',
        showSecondary: true,
        buttonColor: '#CB444A',
        hoverColor: '#A33A3F',
        border: '1px solid #A33A3F',
        hoverBorder: '1px solid white'
      },
      errorMessageBD: {
        headerColor: headerColor || '#d9534f', 
        icon: icon || 'fa-thin fa-hand',
        title: title || '¡Confirmar Acción!',
        showSecondary: true,
        buttonColor: '#CB444A',
        hoverColor: '#A33A3F',
        border: '1px solid #A33A3F',
        hoverBorder: '1px solid white'
      }
    };
    return configs[type] || configs.confirm;
  };

  const config = getTypeConfig();
  const finalHeaderColor = headerColor || config.headerColor;
  const finalIcon = icon || config.icon;
  const finalTitle = title || config.title;
  const showSecondary = config.showSecondary && secondaryButtonText;

  // Manejar click en botón principal
  const handlePrimaryClick = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    }
    if (onClose) {
      onClose();
    }
  };

  // Manejar click en botón secundario
  const handleSecondaryClick = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }
    if (onClose) {
      onClose();
    }
  };

  // Manejar click en botón terciario
  const handleTertiaryClick = () => {
    if (onTertiaryClick) {
      onTertiaryClick();
    }
    if (onClose) {
      onClose();
    }
  };

  // Color del botón principal 
  const buttonColor = primaryButtonColor || finalHeaderColor;

  // Manejadores de hover para el botón primario
  const handleMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = config.hoverColor;
    e.currentTarget.style.border = config.hoverBorder;
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = buttonColor;
    e.currentTarget.style.border = config.border;
  };

  // Manejadores de hover para el botón de cancelar (rojo) - Solo para modales NO error
  const handleCancelMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#364054';
    e.currentTarget.style.borderColor = '#364054';
    e.currentTarget.style.color = 'white';
  };

  const handleCancelMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
    e.currentTarget.style.borderColor = '#7A8797';
    e.currentTarget.style.color = '#7A8797';
  };

  // Manejadores de hover exclusivos para el botón Cancelar del modal de error (gris)
  const handleCancelErrorMouseEnter = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
    e.currentTarget.style.borderColor = '#5a6268';
  };

  const handleCancelErrorMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = '#f5f5f5';
    e.currentTarget.style.borderColor = '#5a6268';
  };

  return (
    <div className="custom-modal-container">
      {/* Header con color */}
      <div
        className="custom-modal-header" 
        style={{ 
            backgroundColor: finalHeaderColor,
            borderBottom: '4px solid #232936'
            
         }}
      >
        <div className="custom-modal-icon">
          <i className={`fa-thin ${finalIcon}`}></i>
        </div>
        <h5 className="custom-modal-title">{finalTitle}</h5>
        {showCloseButton && (
          <button
            type="button"
            className="custom-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        )}
      </div>

      {/* Cuerpo blanco */}
      <div className="custom-modal-body">
        <p className="custom-modal-message">{message}</p>
        {secondaryMessage && (
          <p className="custom-modal-secondary-message">{secondaryMessage}</p>
        )}
      </div>

      {/* Footer gris claro con botones */}
      <div className="custom-modal-footer">
        {showSecondary && (
          <button
            className={`custom-modal-btn ${type === 'error' ? 'btn-cancel-hover' : ''}`}
            onClick={handleSecondaryClick}
            onMouseEnter={type === 'error' ? handleCancelErrorMouseEnter : handleCancelMouseEnter}
            onMouseLeave={type === 'error' ? handleCancelErrorMouseLeave : handleCancelMouseLeave}
            style={type === 'error' ?  {
              backgroundColor: '#f5f5f5',
              color: '#7A8797',
              border: '1px solid #7A8797',
              transition: 'all 0.2s ease'
            } : { 
              backgroundColor: '#f5f5f5',
              color: '#7A8797',
              border: '1px solid #5a6268',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fa-thin fa-arrow-u-turn-up-left me-2"></i>
            {secondaryButtonText}
          </button>
        )}
        {primaryButtonText && (
          <button
            className={`custom-modal-btn custom-modal-btn-primary custom-modal-btn-${type}`}
            onClick={handlePrimaryClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ 
              backgroundColor: buttonColor,
              border: config.border,
            }}
          >
            <i className="fa-thin fa-check me-2"></i>
            {primaryButtonText}
          </button>
        )}
        {tertiaryButtonText && type === 'errorMessageBD' && (
          <button
            className={`custom-modal-btn custom-modal-btn-primary custom-modal-btn-${type}`}
            onClick={handleTertiaryClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ 
              backgroundColor: buttonColor,
              border: config.border,
            }}
          >
            <i className="fa-thin fa-arrow-u-turn-up-left me-2"></i>
            {tertiaryButtonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomModal;
